import { ChevronLeft, ChevronRight } from 'lucide-react'

export interface AdminPaginationProps {
  currentPage: number
  totalPages: number
  /** Shown after “Page X of Y” when set, e.g. “(42 total)”. */
  totalItems?: number
  onPageChange: (page: number) => void
  disabled?: boolean
  className?: string
}

const DELTA = 2

function buildPageButtons(currentPage: number, totalPages: number): (number | 'ellipsis')[] {
  const add = (p: number, range: number[]) => {
    if (p >= 1 && p <= totalPages && !range.includes(p)) range.push(p)
  }
  const raw: number[] = []
  add(1, raw)
  for (let p = currentPage - DELTA; p <= currentPage + DELTA; p++) add(p, raw)
  add(totalPages, raw)
  const sorted = [...new Set(raw)].sort((a, b) => a - b)
  const out: (number | 'ellipsis')[] = []
  for (let i = 0; i < sorted.length; i++) {
    if (i > 0 && sorted[i] - sorted[i - 1] > 1) out.push('ellipsis')
    out.push(sorted[i])
  }
  return out
}

/** Numbered pages + prev/next, matching School Registrations (New registrations). */
export default function AdminPagination({
  currentPage,
  totalPages,
  totalItems,
  onPageChange,
  disabled = false,
  className = '',
}: AdminPaginationProps) {
  if (totalPages <= 1) return null

  const pages = buildPageButtons(currentPage, totalPages)

  return (
    <div
      className={`flex items-center justify-between bg-white rounded-xl p-4 border border-gray-200 flex-wrap gap-3 ${className}`}
    >
      <span className="text-sm text-gray-500">
        Page {currentPage} of {totalPages}
        {typeof totalItems === 'number' && totalItems > 0 && (
          <span className="ml-1">({totalItems} total)</span>
        )}
      </span>
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => onPageChange(Math.max(currentPage - 1, 1))}
          disabled={disabled || currentPage === 1}
          className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 transition-colors"
          aria-label="Previous page"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
        {pages.map((p, i) =>
          p === 'ellipsis' ? (
            <span key={`e-${i}`} className="px-2 text-gray-400">
              …
            </span>
          ) : (
            <button
              key={p}
              type="button"
              onClick={() => onPageChange(p)}
              disabled={disabled}
              className={`min-w-[2.25rem] py-2 px-2 rounded-lg border text-sm font-medium transition-colors ${
                currentPage === p
                  ? 'bg-amber-500 border-amber-500 text-white'
                  : 'border-gray-200 hover:bg-gray-50 text-gray-700'
              } disabled:opacity-50`}
            >
              {p}
            </button>
          ),
        )}
        <button
          type="button"
          onClick={() => onPageChange(Math.min(currentPage + 1, totalPages))}
          disabled={disabled || currentPage === totalPages}
          className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 transition-colors"
          aria-label="Next page"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  )
}
